import { trackBtnClicks, trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { StatusModal } from "shared/UI/StatusModal/StatusModal";
import ViewLoading from "shared/UI/Spinners/ViewLoading";
import { handleWisdomStoreClick } from "core/helpers/mobileStore";
import { useTranslation } from "react-i18next";
import { Platform } from "core/hooks/useMobilePlatform";
import { decodeBase64 } from "core/helpers/utils";

const parseHash = (hash: string) => {
  const parts = hash.replace(/^#/, "").split("&").filter(Boolean);
  const anchor = parts.find((part) => !part.includes("="));
  const params = new URLSearchParams(parts.filter((part) => part.includes("=")).join("&"));
  return { anchor, params };
};

export const Sendgrid = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { anchor, params: hashParams } = parseHash(location.hash);
  const path = searchParams.get("path");
  const orgCode = searchParams.get("orgcode");
  const encodedCorrelationId = searchParams.get("cid") ?? hashParams.get("cid");
  const correlationId = encodedCorrelationId ? decodeBase64(encodedCorrelationId) : undefined;
  const isMobileApp = !!window.flutter_inappwebview;

  const isIOSBrowser = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroidBrowser = /Android/i.test(navigator.userAgent);

  const isMobileBrowser = (!isMobileApp && isIOSBrowser) || (!isMobileApp && isAndroidBrowser);

  const origin = window.location.origin;
  const url = `${origin}/${path}${anchor ? `#${anchor}` : ""}`;

  useEffect(() => {
    trackEvents(EventNames.SENDGRID_EMAIL_CLICK, {
      url,
      ...(orgCode && { orgCode }),
      ...(correlationId && { correlationId }),
    });

    if (isMobileApp || !isMobileBrowser) {
      window.location.href = url;
    }
  }, [isMobileBrowser, isMobileApp, url, orgCode, correlationId]);

  const handleGetInStore = () => {
    if (isIOSBrowser) {
      handleWisdomStoreClick(Platform.ios, "sendgrid_access");
    } else if (isAndroidBrowser) {
      handleWisdomStoreClick(Platform.android, "sendgrid_access");
    } else {
      window.location.href = url;
    }
  };

  const handleOpenApp = () => {
    trackBtnClicks("open_wisdom_app");
    const appScheme = `com.healthassured.wisdom://app/${path}${anchor ? `#${anchor}` : ""}`;
    window.location.href = appScheme;

    setTimeout(() => {
      window.location.href = url;
    }, 3000);
  };

  const handleDismiss = () => {
    trackBtnClicks("dismiss_sendgrid_modal");
    window.location.href = url;
  };

  if (isMobileApp || !isMobileBrowser) {
    return <ViewLoading label={t("auth.loading")} />;
  }

  return (
    <StatusModal
      data={t("sendgrid", { returnObjects: true })}
      status="info"
      handleConfirm={handleOpenApp}
      handleCancel={handleGetInStore}
      handleDismiss={handleDismiss}
      showDismiss
    />
  );
};
